<template>
  <div class="main-content" v-loading="pageLoading">
    <!-- 表单卡片区域 -->
    <el-form
      ref="formRef"
      :model="form"
      :rules="formRules"
      label-width="110px"
      size="medium"
      label-position="right"
      class="course-large-form"
    >
      <!-- 卡片一：基本信息 -->
      <el-card class="form-card marginBottom20">
        <div slot="header" class="card-header">
          <span class="card-title">基本信息</span>
        </div>

        <el-row :gutter="20">
          <!-- 课程标题 -->
          <el-col :span="24">
            <el-form-item label="课程标题" prop="title">
              <el-input
                v-model="form.title"
                maxlength="100"
                placeholder="请输入课程标题"
                clearable
                show-word-limit
                style="width: 100%"
              ></el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <!-- 一级分类 -->
          <el-col :span="8">
            <el-form-item label="课程分类(一)" prop="firstLevelClassifyId">
              <el-select
                v-model="form.firstLevelClassifyId"
                placeholder="请选择一级分类"
                style="width: 100%"
                @change="handleFirstLevelChange"
              >
                <el-option
                  v-for="item in firstLevelOptions"
                  :key="item.id"
                  :label="item.name"
                  :value="item.id"
                ></el-option>
              </el-select>
            </el-form-item>
          </el-col>

          <!-- 二级分类 -->
          <el-col :span="8">
            <el-form-item label="课程分类(二)" prop="secondLevelClassifyId">
              <el-select
                v-model="form.secondLevelClassifyId"
                placeholder="请选择二级分类"
                clearable
                style="width: 100%"
                :disabled="!form.firstLevelClassifyId"
                @change="handleSecondLevelChange"
              >
                <el-option
                  v-for="item in secondLevelOptions"
                  :key="item.id"
                  :label="item.name"
                  :value="item.id"
                ></el-option>
              </el-select>
            </el-form-item>
          </el-col>

          <!-- 三级分类 -->
          <el-col :span="8">
            <el-form-item label="课程分类(三)" prop="thirdLevelClassifyId">
              <el-select
                v-model="form.thirdLevelClassifyId"
                placeholder="请选择三级分类"
                clearable
                style="width: 100%"
                :disabled="!form.secondLevelClassifyId"
              >
                <el-option
                  v-for="item in thirdLevelOptions"
                  :key="item.id"
                  :label="item.name"
                  :value="item.id"
                ></el-option>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <!-- 权益分类 -->
          <el-col :span="12">
            <el-form-item label="权益分类" prop="equityClassifyIds">
              <el-select
                v-model="form.equityClassifyIds"
                multiple
                placeholder="请选择权益分类"
                style="width: 100%"
                @change="handleEquityChange"
              >
                <el-option
                  v-for="item in equityOptions"
                  :key="item.id"
                  :label="item.name"
                  :value="item.id"
                ></el-option>
              </el-select>
            </el-form-item>
          </el-col>

          <!-- 排序 -->
          <el-col :span="12">
            <el-form-item label="排序值" prop="sort">
              <el-input-number
                v-model="form.sort"
                :min="1"
                :max="9999"
                controls-position="right"
                style="width: 100%"
              ></el-input-number>
              <div class="upload-tip">排序值越大越靠前</div>
            </el-form-item>
          </el-col>
        </el-row>
      </el-card>

      <!-- 卡片二：多媒体与状态配置 -->
      <el-card class="form-card marginBottom20">
        <div slot="header" class="card-header">
          <span class="card-title">封面与状态</span>
        </div>

        <el-row :gutter="20">
          <!-- 课程封面 -->
          <el-col :span="14">
            <el-form-item label="课程封面" prop="image">
              <FormImgUpload
                :url="form.image ? [form.image] : []"
                :limit="1"
                :maxSize="5"
                accept=".png,.jpg,.jpeg,.gif"
                @upload="handleCoverUpload"
              />
              <div class="upload-tip">
                建议尺寸 266*150px，支持 JPG/PNG/GIF，大小不超过 5MB
              </div>
            </el-form-item>
          </el-col>

          <!-- 上架状态 -->
          <el-col :span="10">
            <el-form-item label="上架状态" prop="state">
              <el-radio-group v-model="form.state">
                <el-radio :label="1">上架</el-radio>
                <el-radio :label="2">下架</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>
      </el-card>

      <!-- 卡片三：课程视频明细资源 -->
      <el-card class="form-card marginBottom20">
        <div slot="header" class="card-header flex justify-between align-items">
          <span class="card-title">课程视频</span>
          <el-button
            type="primary"
            size="small"
            icon="el-icon-plus"
            @click="addVideoItem"
          >
            添加视频
          </el-button>
        </div>

        <!-- 空数据提示 -->
        <div
          v-if="form.videoDetailList.length === 0"
          class="empty-video-container flex align-items justify-center"
        >
          <div class="text-center">
            <i class="el-icon-video-cameraempty-icon"></i>
            <p class="empty-text">
              当前暂未添加任何视频，请点击右上方“添加视频”按钮开始上传。
            </p>
          </div>
        </div>

        <!-- 动态卡片列表 -->
        <div v-else class="video-grid">
          <el-row :gutter="20">
            <el-col
              v-for="(item, index) in form.videoDetailList"
              :key="index"
              :span="24"
              class="video-card-col"
            >
              <div class="video-card-item">
                <!-- 头部操作区 -->
                <div class="video-item-header flex justify-between align-items">
                  <span class="video-badge">课程视频 {{ index + 1 }}</span>
                  <el-button
                    type="text"
                    icon="el-icon-delete"
                    class="btn-remove-video"
                    @click="removeVideoItem(index)"
                  >
                    移除
                  </el-button>
                </div>

                <!-- 卡片明细表单 -->
                <div class="video-item-body">
                  <!-- 上传视频 -->
                  <div class="video-upload-box">
                    <div class="upload-label">
                      上传视频文件 <span class="red-star">*</span>
                    </div>
                    <FormVideoUpload
                      :url="item.fileUrl ? [item.fileUrl] : []"
                      :limit="1"
                      :maxSize="10240"
                      accept=".mp4,.webm,.avi"
                      @upload="(res) => handleVideoUpload(res, item)"
                    />
                    <div class="upload-tip">
                      支持 MP4 格式，单文件不超过 10GB
                    </div>
                  </div>

                  <!-- 视频标题 -->
                  <div class="video-title-box marginTop15">
                    <div class="upload-label">
                      视频标题 <span class="red-star">*</span>
                    </div>
                    <el-input
                      v-model="item.fileName"
                      placeholder="请输入视频标题"
                      maxlength="100"
                      clearable
                      size="small"
                    ></el-input>
                  </div>
                </div>
              </div>
            </el-col>
          </el-row>
        </div>
      </el-card>

      <!-- 底部提交操作栏 -->
      <div class="bottom-action-bar flex justify-center">
        <el-button @click="goBack" size="medium" style="margin-right: 20px"
          >取 消</el-button
        >
        <el-button
          type="primary"
          @click="submit"
          :loading="showLoadingForm"
          size="medium"
        >
          保 存
        </el-button>
      </div>
    </el-form>
  </div>
</template>

<script>
import {
  courseInfoAdd,
  courseInfoUpdate,
  courseInfoDetail,
} from "@/api/course/info";
import { courseClassifyListAll } from "@/api/course/classify";
import { equityListClassify } from "@/api/equity/manage";
import FormImgUpload from "@/components/formComponents/formImgUpload";
import FormVideoUpload from "@/components/formComponents/formVideoUpload";

export default {
  name: "CourseInfoForm",
  components: {
    FormImgUpload,
    FormVideoUpload,
  },
  data() {
    return {
      pageLoading: false,
      showLoadingForm: false, // 防抖提交
      classifyTreeData: [], // 全量树
      equityOptions: [], // 权益下拉框
      form: {
        id: "",
        title: "",
        firstLevelClassifyId: "",
        secondLevelClassifyId: "",
        thirdLevelClassifyId: "", // 课程三级分类id
        equityClassifyId: "",
        equityClassifyIds: [], // 临时绑定的多选权益分类数组
        equityClassName: "",
        sort: 1,
        image: "",
        state: 1,
        isFeatured: 1, // 默认精品：1-精品；2-普通
        isHome: 2, // 默认不推荐首页：1-是；2-否
        videoDetailList: [],
      },
      formRules: {
        title: [
          { required: true, message: "请输入课程标题", trigger: "blur" },
          { max: 100, message: "标题长度不能超过 100 个字符", trigger: "blur" },
        ],
        firstLevelClassifyId: [
          { required: true, message: "请选择一级分类", trigger: "change" },
        ],
        sort: [{ required: true, message: "请输入排序值", trigger: "blur" }],
        image: [
          { required: true, message: "请上传课程封面", trigger: "change" },
        ],
        state: [
          { required: true, message: "请选择上架状态", trigger: "change" },
        ],
      },
    };
  },
  computed: {
    // 动态一级分类
    firstLevelOptions() {
      return this.classifyTreeData.map((item) => ({
        id: item.id,
        name: item.name,
      }));
    },

    // 动态二级级联
    secondLevelOptions() {
      if (!this.form.firstLevelClassifyId) return [];
      const parentNode = this.classifyTreeData.find(
        (item) => item.id === this.form.firstLevelClassifyId
      );
      if (parentNode && Array.isArray(parentNode.children)) {
        return parentNode.children.map((item) => ({
          id: item.id,
          name: item.name,
        }));
      }
      return [];
    },

    // 动态三级级联
    thirdLevelOptions() {
      if (!this.form.firstLevelClassifyId || !this.form.secondLevelClassifyId)
        return [];
      const firstNode = this.classifyTreeData.find(
        (item) => item.id === this.form.firstLevelClassifyId
      );
      if (firstNode && Array.isArray(firstNode.children)) {
        const secondNode = firstNode.children.find(
          (item) => item.id === this.form.secondLevelClassifyId
        );
        if (secondNode && Array.isArray(secondNode.children)) {
          return secondNode.children.map((item) => ({
            id: item.id,
            name: item.name,
          }));
        }
      }
      return [];
    },
  },
  created() {
    this.initData();
  },
  methods: {
    // 初始化
    async initData() {
      this.pageLoading = true;
      try {
        await Promise.all([this.getClassifyTree(), this.getEquityList()]);
        console.log(this.$route.query.id, "this.$route.query.id");

        // 若路由带有 id，则说明是编辑模式
        const courseId = this.$route.query.id;
        if (courseId) {
          await this.getCourseDetail(courseId);
        }
      } catch (error) {
        console.error("加载表单初始配置失败:", error);
      } finally {
        this.pageLoading = false;
      }
    },

    // 路由返回列表
    goBack() {
      this.$router.push("/course/info");
    },

    // 加载分类树
    async getClassifyTree() {
      const res = await courseClassifyListAll();
      if (res.code === 200 && Array.isArray(res.data)) {
        this.classifyTreeData = res.data;
      }
    },

    // 加载权益分类
    async getEquityList() {
      const res = await equityListClassify();
      if (res.code === 200 && res.data) {
        this.equityOptions = res.data.records || res.data || [];
      }
    },

    // 根据课程 id 获取详情数据回显
    async getCourseDetail(id) {
      const res = await courseInfoDetail(id);
      if (res.code === 200 && res.data) {
        const detail = res.data;
        this.form = {
          id: detail.id,
          title: detail.title,
          firstLevelClassifyId: detail.firstLevelClassifyId || "",
          secondLevelClassifyId: detail.secondLevelClassifyId || "",
          thirdLevelClassifyId: detail.thirdLevelClassifyId || "",
          equityClassifyId: detail.equityClassifyId || "",
          equityClassifyIds: detail.equityClassifyId
            ? detail.equityClassifyId.split(",")
            : [],
          equityClassName: detail.equityClassName || "",
          sort: detail.sort || 1,
          image: detail.image || "",
          state: detail.state || 1,
          isFeatured: detail.isFeatured || 2,
          isHome: detail.isHome || 2,
          videoDetailList: Array.isArray(detail.videoDetailList)
            ? detail.videoDetailList.map((v) => ({
                id: v.id,
                fileName: v.fileName || "",
                fileUrl: v.fileUrl || "",
                durationNum: v.durationNum || v.duration || 0,
                sort: v.sort || 1,
              }))
            : [],
        };
      } else {
        this.$message.error("获取课程详情失败，无法编辑。");
        this.goBack();
      }
    },

    /**
     * 一级分类改变事件，清空已选的二级和三级分类
     */
    handleFirstLevelChange() {
      this.form.secondLevelClassifyId = "";
      this.form.thirdLevelClassifyId = "";
    },

    /**
     * 二级分类改变事件，清空已选的三级分类
     */
    handleSecondLevelChange() {
      this.form.thirdLevelClassifyId = "";
    },

    /**
     * 权益分类多选改变事件，遍历已选项翻译对应名称，并以逗号拼接后赋值给表单字段
     * @param {Array<string|number>} vals - 当前选中的权益分类 ID 数组
     */
    handleEquityChange(vals) {
      if (Array.isArray(vals) && vals.length > 0) {
        const names = [];
        vals.forEach((val) => {
          const found = this.equityOptions.find((item) => item.id === val);
          if (found) {
            names.push(found.name);
          }
        });
        this.form.equityClassName = names.join(",");
      } else {
        this.form.equityClassName = "";
      }
    },

    // 封面上传
    handleCoverUpload(res) {
      if (Array.isArray(res)) {
        this.form.image = res[0] || "";
      } else {
        this.form.image = res || "";
      }
      if (this.$refs.formRef) {
        this.$refs.formRef.validateField("image");
      }
    },

    // 新增一个视频明细
    addVideoItem() {
      this.form.videoDetailList.push({
        fileName: "",
        fileUrl: "",
        durationNum: 0,
        sort: this.form.videoDetailList.length + 1,
      });
    },

    // 删除某个视频明细
    removeVideoItem(index) {
      this.form.videoDetailList.splice(index, 1);
      // 重新对 sort 编号
      this.form.videoDetailList.forEach((v, idx) => {
        v.sort = idx + 1;
      });
    },

    // 视频文件上传回调
    handleVideoUpload(res, item) {
      let videoUrl = "";
      if (Array.isArray(res)) {
        videoUrl = res[0] || "";
      } else {
        videoUrl = res || "";
      }
      item.fileUrl = videoUrl;

      // 若视频标题为空，提取并自动填入文件名
      if (videoUrl && !item.fileName) {
        try {
          const decodeUrl = decodeURIComponent(videoUrl);
          const name = decodeUrl.substring(decodeUrl.lastIndexOf("/") + 1);
          const cleanName =
            name.substring(0, name.lastIndexOf(".")) || "未命名视频";
          item.fileName = cleanName;
        } catch (e) {
          item.fileName = "视频课程";
        }
      }

      // 异步读取视频播放时长
      if (videoUrl) {
        this.getVideoDuration(videoUrl).then((duration) => {
          item.durationNum = duration;
        });
      }
    },

    // 异步提取时长数
    getVideoDuration(url) {
      return new Promise((resolve) => {
        const video = document.createElement("video");
        video.src = url;
        video.addEventListener("loadedmetadata", () => {
          resolve(Math.round(video.duration));
        });
        video.addEventListener("error", () => {
          resolve(0);
        });
      });
    },

    // 对动态多视频进行校验
    validateVideoList() {
      const list = this.form.videoDetailList;
      if (!list || list.length === 0) {
        this.$message.error("请至少添加一个课程视频!");
        return false;
      }
      for (let i = 0; i < list.length; i++) {
        const v = list[i];
        if (!v.fileUrl) {
          this.$message.error(`课程视频 ${i + 1} 未上传视频文件`);
          return false;
        }
        if (!v.fileName || !v.fileName.trim()) {
          this.$message.error(`请输入课程视频 ${i + 1} 的视频标题`);
          return false;
        }
      }
      return true;
    },

    // 保存提交
    submit() {
      this.$refs.formRef.validate((valid) => {
        if (valid) {
          if (!this.validateVideoList()) return;

          if (this.showLoadingForm) return;
          this.showLoadingForm = true;

          const submitData = {
            ...this.form,
            title: this.form.title.trim(),
            equityClassifyId: this.form.equityClassifyIds.join(","),
          };
          delete submitData.equityClassifyIds;

          const requestApi = submitData.id ? courseInfoUpdate : courseInfoAdd;

          requestApi(submitData)
            .then((res) => {
              this.showLoadingForm = false;
              if (res.code === 200) {
                this.$message.success("保存成功!");
                this.goBack();
              }
            })
            .catch(() => {
              this.showLoadingForm = false;
            });
        }
      });
    },
  },
};
</script>

<style scoped>
.main-content {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: calc(100vh - 100px);
}

.form-header {
  background-color: #ffffff;
  padding: 15px 20px;
  border-radius: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.form-card {
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
  border-radius: 4px;
}

.card-header {
  border-left: 4px solid #409eff;
  padding-left: 10px;
}
.card-title {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
}

.upload-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
  line-height: 1.4;
}

/* 视频部分结构 */
.empty-video-container {
  height: 150px;
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
  color: #909399;
}
.empty-icon {
  font-size: 32px;
  color: #c0c4cc;
  margin-bottom: 10px;
}
.empty-text {
  font-size: 13px;
}

.video-grid {
  max-height: 550px;
  overflow-y: auto;
  padding: 5px;
}
.video-card-col {
  margin-bottom: 20px;
}
.video-card-item {
  background-color: #ffffff;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  transition: all 0.3s;
}
.video-card-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}
.video-item-header {
  background-color: #fcfcfc;
  padding: 10px 15px;
  border-bottom: 1px solid #f2f6fc;
}
.video-badge {
  font-weight: bold;
  color: #409eff;
  font-size: 14px;
}
.btn-remove-video {
  color: #f56c6c;
  padding: 0;
  font-size: 13px;
}
.video-item-body {
  padding: 15px;
}
.upload-label {
  font-size: 13px;
  color: #606266;
  font-weight: bold;
  margin-bottom: 8px;
}
.red-star {
  color: #f56c6c;
}
.duration-label {
  font-size: 13px;
  color: #909399;
  margin-right: 8px;
}
.duration-value {
  font-size: 13px;
  font-weight: bold;
  color: #67c23a;
}

/* 底部控制按钮 */
.bottom-action-bar {
  background-color: #ffffff;
  padding: 15px;
  border-radius: 4px;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.03);
  margin-top: 30px;
}

/* 常用间距 */
.marginBottom20 {
  margin-bottom: 20px;
}
.marginTop10 {
  margin-top: 10px;
}
.marginTop15 {
  margin-top: 15px;
}
.flex {
  display: flex;
}
.align-items {
  align-items: center;
}
.justify-between {
  justify-content: space-between;
}
.justify-center {
  justify-content: center;
}
.text-center {
  text-align: center;
}
</style>
